import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { isAdmin as checkIsAdmin, isUser as checkIsUser } from '../types';
import Logo from './Logo';
import UserProfile from './UserProfile';
import ProfileModal from './ProfileModal';
import NotificationBell from './NotificationBell';
import ExchangeRateIndicator from './ExchangeRateIndicator';
import PageContainer from './layout/PageContainer';

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, userType, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);

  const handleLogout = async (): Promise<void> => {
    try {
      await logout();
      setIsMobileMenuOpen(false);
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleLogoClick = (): void => {
    navigate('/');
  };

  const getUserDisplayName = (): string => {
    if (!user) return 'User';
    if (checkIsAdmin(user)) {
      return user.name || user.email.split('@')[0];
    }
    if (checkIsUser(user)) {
      return user.contact_person || user.email.split('@')[0];
    }
    return (user as any).email?.split('@')[0] || 'User';
  };

  return (
    // 래퍼는 auto height 유지 — 모바일 메뉴 드롭다운이 헤더 아래로 펼쳐져야 함
    <header className="fixed inset-x-0 top-0 z-50 pt-[env(safe-area-inset-top)]">
      <PageContainer padded="none">
        {/* 높이는 --app-header-h 로 고정 — Layout 의 본문 여백 계산과 단일 소스 */}
        <div className="flex items-center justify-between h-[var(--app-header-h)]">
          {/* Logo */}
          <div className="flex items-center">
            <button onClick={handleLogoClick} className="cursor-pointer">
              {/* 자손 셀렉터로 로고 높이를 반응형 오버라이드 (특이도 0,1,1 > .h-20 이라 ! 불필요) */}
              <Logo size="xlarge" variant="compact" className="[&_img]:h-10 md:[&_img]:h-20" />
            </button>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-md font-medium transition-colors duration-200 ${
                location.pathname === '/'
                  ? 'text-np-600'
                  : 'text-gray-700 hover:text-np-600'
              }`}
            >
              Home
            </Link>

            {/* New Formulations — 목록·상세 전체 공개(미인증 포함) */}
            <Link
              to="/formulations"
              className={`text-md font-medium transition-colors duration-200 ${
                location.pathname.startsWith('/formulations')
                  ? 'text-np-600'
                  : 'text-gray-700 hover:text-np-600'
              }`}
            >
              New Formulations
            </Link>

            {!isAuthenticated && (
              <Link
                to="/sample-request"
                className={`text-md font-medium transition-colors ${
                  location.pathname === '/sample-request'
                    ? 'text-np-600'
                    : 'text-gray-700 hover:text-np-600'
                }`}
              >
                Request Sample
              </Link>
            )}

            {isAuthenticated && userType === 'brand' && (
              <Link
                to="/sample-request"
                className={`text-md font-medium transition-colors ${
                  location.pathname === '/sample-request'
                    ? 'text-np-600'
                    : 'text-gray-700 hover:text-np-600'
                }`}
              >
                Request Sample
              </Link>
            )}

            {isAuthenticated && userType === 'admin' && (
              <>
                <Link
                  to="/sample-request"
                  className={`text-md font-medium transition-colors ${
                    location.pathname === '/sample-request'
                      ? 'text-np-600'
                      : 'text-gray-700 hover:text-np-600'
                  }`}
                >
                  Request Sample
                </Link>
                <Link
                  to="/admin/management"
                  className={`text-md font-medium transition-colors ${
                    location.pathname.includes('/admin/management')
                      ? 'text-np-600'
                      : 'text-gray-700 hover:text-np-600'
                  }`}
                >
                  Management
                </Link>
                <Link
                  to="/admin/agent"
                  className={`text-md font-medium transition-colors ${
                    location.pathname.includes('/admin/agent')
                      ? 'text-np-600'
                      : 'text-gray-700 hover:text-np-600'
                  }`}
                >
                  Agent
                </Link>
                {user && 'role' in user && user.role === 'admin' && (
                  <Link
                    to="/admin/settings"
                    className={`text-md font-medium transition-colors ${
                      location.pathname.includes('/admin/settings')
                        ? 'text-np-600'
                        : 'text-gray-700 hover:text-np-600'
                    }`}
                  >
                    Admin
                  </Link>
                )}
              </>
            )}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            <ExchangeRateIndicator />
            {isAuthenticated ? (
              <>
                <NotificationBell />
                <UserProfile />
              </>
            ) : (
              <Link
                to="/login"
                className={`text-md font-medium transition-colors ${
                  location.pathname === '/login'
                    ? 'text-np-600'
                    : 'text-gray-700 hover:text-np-600'
                }`}
              >
                Sign In
              </Link>
            )}
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button
                type="button"
                className="p-2 rounded-md text-gray-400 hover:text-gray-500"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <span className="sr-only">Open menu</span>
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </PageContainer>

      {/* Mobile menu — 항목이 많아도 화면을 넘지 않도록 자체 스크롤 + 하단 safe-area */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200 max-h-[calc(100dvh-var(--app-header-h))] overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          <div className="px-4 py-2 space-y-1">
            <Link
              to="/"
              className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Home
            </Link>

            {/* New Formulations — 목록 전체 공개 */}
            <Link
              to="/formulations"
              className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              New Formulations
            </Link>

            {!isAuthenticated && (
              <Link
                to="/sample-request"
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Request Sample
              </Link>
            )}
            
            {isAuthenticated ? (
              <>
                {/* User Info */}
                <div className="px-3 py-3 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900">{getUserDisplayName()}</div>
                  <div className="text-xs text-gray-500">{user?.email}</div>
                </div>

                {userType === 'brand' && (
                  <Link
                    to="/sample-request"
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    Dashboard
                  </Link>
                )}


                {userType === 'admin' && (
                  <>
                    <Link
                      to="/sample-request"
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      Request Sample
                    </Link>
                    <Link
                      to="/admin/management"
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Management
                    </Link>
                    <Link
                      to="/admin/agent"
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Agent
                    </Link>
                    {user && 'role' in user && user.role === 'admin' && (
                      <Link
                        to="/admin/settings"
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        Admin
                      </Link>
                    )}
                  </>
                )}

                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowProfileModal(true);
                  }}
                  className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600 hover:bg-gray-50"
                >
                  <svg className="w-4 h-4 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  Profile
                </button>

                <div className="border-t border-gray-100 mt-1 pt-1">
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                  >
                    <svg className="w-4 h-4 mr-3 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </>
            ) : (
              <Link
                to="/login"
                className="block px-3 py-2 text-sm font-medium text-gray-700 hover:text-np-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Sign In
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </header>
  );
};

export default Header;